import React, {useState, useEffect} from 'react';
import Pane from "../../../Components/Pane";
import {cloneDeep} from "lodash";
import {
    DEFAULT_ERROR_MESSAGE,
    TERMS_AND_CONDITIONS,
    PRIVACY_POLICY, DATE_FORMATS, JOURNEYS, prependJourneyToRouteName,
} from "../../../constants";
import InvisiblePane from "../../../Components/InvisiblePane";
import PageLoadingPanel from "../../../Components/PageLoadingPanel";
import ProgressBar from "../../../Components/ProgressBar";
import PrimaryTitle from "../../../Components/PrimaryTitle";
import Spacer from "../../../Components/Spacer";
import Button from "../../../Components/Button";
import FormInput from "../../../Components/Forms/FormInput";
import SecondaryTitle from "../../../Components/SecondaryTitle";
import Checkbox from "../../../Components/Forms/Inputs/Checkbox";
import {default as FieldError} from "../../../Components/Forms/Error";
import Error from "../../../Components/Error";
import AgentPanel from "../../../Components/AgentPanel";
import {format, parse, subYears} from "date-fns";

const PersonalDetailsPage = (props) => {
    const {
        nextPage    = () => {},
        journey     = JOURNEYS.AGENT,
    } = props;

    const PAGE_TITLE            = 'Basic Details';
    const CURRENT_STEP_NUMBER   = 1;
    const TOTAL_STEP_NUMBER     = 4;

    const [data, setData] = useState({
        first_name:                 '',
        last_name:                  '',
        mobile_number:              '',
        email:                      '',
        date_of_birth:              '',
        address_line_1:             '',
        address_line_2:             '',
        town_city:                  '',
        state_county:               '',
        postcode_zipcode:           '',
        country:                    'UNITED KINGDOM',
        terms_accepted:             0,
        gdpr_email_enabled:         0,
    });
    const [countries, setCountries]         = useState(null);
    const [loaded, setLoaded]               = useState(false);
    const [errors, setErrors]               = useState(null);
    const [errorMessage, setErrorMessage]   = useState(null);
    const [submitting, setSubmitting]       = useState(false);

    useEffect(() => {
        loadCountries();
        loadPageData();
    }, []);

    useEffect(() => {
        if (submitting) {
            submit();
        }
    }, [submitting]);

    const loadCountries = () => {
        axios.get(route('web-api.select.countries').toString())
            .then((response) => {
                if (!!response && !!response.data) {
                    setCountries(response.data);
                }
            })
            .catch((error) => {
                console.log(error);
            }).finally(() => {
            setLoaded(true);
        });
    }

    const loadPageData = () => {
        axios.get(route(prependJourneyToRouteName(journey, 'session.personal-details.get'), {session: window.session}).toString())
            .then((response) => {
                if (!!response && !!response.data) {
                    setData(response.data);
                }
            })
            .catch((error) => {
                console.log(error);
            }).finally(() => {
                setLoaded(true);
            });
    }

    const submit = () => {
        setErrorMessage(null);
        setErrors(null);

        console.log('data', data);

        axios.patch(route(prependJourneyToRouteName(journey, 'session.personal-details.update'), {session: window.session}).toString(), data)
            .then((response) => {
                setSubmitting(false);
                nextPage();
            })
            .catch((error) => {
                if (!!error && !!error.response && !!error.response.data) {
                    if (!!error.response.data.message) {
                        setErrorMessage(error.response.data.message);
                    }

                    if (!!error.response.data.errors) {
                        setErrors(error.response.data.errors);
                    }

                    setSubmitting(false);
                } else {
                    console.log(error);
                    setErrorMessage(DEFAULT_ERROR_MESSAGE);
                }
            });
    }

    const updateField = (field, value) => {
        let clone = cloneDeep(data);

        switch (field) {
            default:
                clone[field] = value;
                break;
        }

        setData(clone);
    }

    const transformDateString = (value, formatIn, formatOut) => {
        if (value === undefined || value === null || value === '') {
            return '';
        }

        return format(parse(value, formatIn, new Date()), formatOut);
    }

    const renderError = () => {
        if (!errorMessage) {
            return null;
        }

        return <Error message={errorMessage} />
    }

    if (!loaded || !countries) {
        return (
            <PageLoadingPanel
                pageTitle={PAGE_TITLE}
                currentStepNumber={CURRENT_STEP_NUMBER}
                totalStepNumber={TOTAL_STEP_NUMBER}
                upperTextComponent={null}
            />
        )
    }

    return (
        <React.Fragment>
            {renderError()}
            <Pane>
                <ProgressBar
                    title={PAGE_TITLE}
                    currentStepNumber={CURRENT_STEP_NUMBER}
                    totalStepNumber={TOTAL_STEP_NUMBER}
                />
                <PrimaryTitle>{PAGE_TITLE}</PrimaryTitle>
                <div className={'row m-0 p-0'}>
                    <div className={'col-12 col-md-6 m-0 p-0 pe-md-2'}>
                        <FormInput
                            name={'first_name'}
                            label={'First name'}
                            value={data.first_name}
                            type={'text'}
                            onChange={(value) => updateField('first_name', value)}
                            disabled={submitting || !loaded}
                            error={errors}
                        />
                    </div>
                    <div className={'col-12 col-md-6 m-0 p-0 ps-md-2'}>
                        <FormInput
                            name={'last_name'}
                            label={'Last name'}
                            value={data.last_name}
                            type={'text'}
                            onChange={(value) => updateField('last_name', value)}
                            disabled={submitting || !loaded}
                            error={errors}
                        />
                    </div>
                </div>
                <FormInput
                    name={'date_of_birth'}
                    label={'Date of birth'}
                    value={transformDateString(data.date_of_birth, DATE_FORMATS.DB_DATE, DATE_FORMATS.DATE)}
                    type={'date'}
                    onChange={(value) => updateField('date_of_birth', transformDateString(value, DATE_FORMATS.DATE, DATE_FORMATS.DB_DATE))}
                    disabled={submitting || !loaded}
                    error={errors}
                    maxDate={subYears(new Date(), 18)}
                />
                <div className={'row m-0 p-0'}>
                    <div className={'col-12 col-md-6 m-0 p-0 pe-md-2'}>
                        <FormInput
                            name={'email'}
                            label={'Email'}
                            value={data.email}
                            type={'email'}
                            onChange={(value) => updateField('email', value)}
                            disabled={submitting || !loaded}
                            error={errors}
                        />
                    </div>
                    <div className={'col-12 col-md-6 m-0 p-0 ps-md-2'}>
                        <FormInput
                            name={'mobile_number'}
                            label={'Mobile number'}
                            value={data.mobile_number}
                            type={'text'}
                            onChange={(value) => updateField('mobile_number', value)}
                            disabled={submitting || !loaded}
                            error={errors}
                        />
                    </div>
                </div>
                <Spacer />
                <SecondaryTitle>Address</SecondaryTitle>
                <div className={'row m-0 p-0'}>
                    <div className={'col-12 col-md-6 m-0 p-0 pe-md-2'}>
                        <FormInput
                            name={'address_line_1'}
                            label={'Address line 1'}
                            value={data.address_line_1}
                            type={'text'}
                            onChange={(value) => updateField('address_line_1', value)}
                            disabled={submitting || !loaded}
                            error={errors}
                        />
                    </div>
                    <div className={'col-12 col-md-6 m-0 p-0 ps-md-2'}>
                        <FormInput
                            name={'address_line_2'}
                            label={'Address line 2'}
                            value={data.address_line_2}
                            type={'text'}
                            onChange={(value) => updateField('address_line_2', value)}
                            disabled={submitting || !loaded}
                            error={errors}
                        />
                    </div>
                </div>
                <div className={'row m-0 p-0'}>
                    <div className={'col-12 col-md-6 m-0 p-0 pe-md-2'}>
                        <FormInput
                            name={'town_city'}
                            label={'Town / City'}
                            value={data.town_city}
                            type={'text'}
                            onChange={(value) => updateField('town_city', value)}
                            disabled={submitting || !loaded}
                            error={errors}
                        />
                    </div>
                    <div className={'col-12 col-md-6 m-0 p-0 ps-md-2'}>
                        <FormInput
                            name={'state_county'}
                            label={'County'}
                            value={data.state_county}
                            type={'text'}
                            onChange={(value) => updateField('state_county', value)}
                            disabled={submitting || !loaded}
                            error={errors}
                        />
                    </div>
                </div>
                <div className={'row m-0 p-0'}>
                    <div className={'col-12 col-md-6 m-0 p-0 pe-md-2'}>
                        <FormInput
                            name={'postcode_zipcode'}
                            label={'Postcode'}
                            value={data.postcode_zipcode}
                            type={'text'}
                            onChange={(value) => updateField('postcode_zipcode', value)}
                            disabled={submitting || !loaded}
                            error={errors}
                        />
                    </div>
                    <div className={'col-12 col-md-6 m-0 p-0 ps-md-2'}>
                        <FormInput
                            name={'country'}
                            label={'Country'}
                            value={data.country}
                            type={'select'}
                            options={countries}
                            onChange={(value) => updateField('country', value)}
                            disabled={submitting || !loaded}
                            error={errors}
                        />
                    </div>
                </div>
                <Spacer />
                <SecondaryTitle>Newsletter</SecondaryTitle>
                <div className={"form-input-container"}>
                    <Checkbox
                        name={'gdpr_email_enabled'}
                        label={('Yes please, I want to sign up for the free small business newsletter and agree to receive email marketing communications. I understand my personal data will be treated in accordance with our <a href="'+PRIVACY_POLICY+'" target="_blank">Privacy Policy</a>.')}
                        checked={data.gdpr_email_enabled === true || data.gdpr_email_enabled === 'true' || data.gdpr_email_enabled === 1 || data.gdpr_email_enabled === '1'}
                        onChange={(value) => updateField('gdpr_email_enabled', value === true ? 1 : 0)}
                        disabled={submitting || !loaded}
                    />
                    <FieldError error={!!errors && !!errors.gdpr_email_enabled ? errors.gdpr_email_enabled : null} />
                </div>
                <Spacer />
                <SecondaryTitle>Terms and conditions</SecondaryTitle>
                <div className={"form-input-container"} style={{marginBottom: 0}}>
                    <Checkbox
                        name={'terms_accepted'}
                        label={('I confirm I agree to the <a href="'+TERMS_AND_CONDITIONS+'" target="_blank">Terms of use</a> for this subscription.')}
                        checked={data.terms_accepted === true || data.terms_accepted === 'true' || data.terms_accepted === 1 || data.terms_accepted === '1'}
                        onChange={(value) => updateField('terms_accepted', value === true ? 1 : 0)}
                        disabled={submitting || !loaded}
                    />
                    <FieldError error={!!errors && !!errors.terms_accepted ? errors.terms_accepted : null} />
                </div>
            </Pane>
            {renderError()}
            <InvisiblePane style={{paddingTop: '59px', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                <Button
                    label={submitting ? 'Submitting...' : 'Next'}
                    disabled={submitting}
                    onClick={() => setSubmitting(true)}
                />
            </InvisiblePane>
            <AgentPanel journey={journey} />
        </React.Fragment>
    );
}

export default PersonalDetailsPage;
